import { Question } from '@/utils/api-objects/Question';
import { ANSWERS_OPENING_INPUT } from '@/utils/Constants';
import { QuestionCategory, QuestionScoreCategory, QuestionType } from '@/utils/enums/Surveys';
import { useEffect, useState } from 'react';

interface UseSurveyResponsesProps {
    currentQuestion?: Question;
    goToNextQuestion: () => void;
    setQuestions?: React.Dispatch<React.SetStateAction<Question[] | undefined>>;
}

export const useSurveyResponses = ({ currentQuestion, goToNextQuestion, setQuestions }: UseSurveyResponsesProps) => {
    const [singleValueAnswer, setSingleValueAnswer] = useState("");
    const [openParagraphAnswer, setOpenParagraphAnswer] = useState("");
    const [selectedRadioAnswer, setSelectedRadioAnswer] = useState<string | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set());
    const [customInputValue, setCustomInputValue] = useState<string>("");
    const [surveyResponses, setSurveyResponses] = useState<Map<string, Set<string>>>(new Map());
    const [hasAnswered, setHasAnswered] = useState(false);
    const [choiceBecomesInput, setChoiceBecomesInput] = useState(false);
    const [isOldAnswerCustom, setIsOldAnswerCustom] = useState(false);
    const [oldAnswerCustom, setOldAnswerCustom] = useState("");

    // Reset state when question changes
    useEffect(() => {
        if (currentQuestion) {
            const answers = surveyResponses.get(currentQuestion.id!) || new Set<string>();
            setChoiceBecomesInput(false);
            setCustomInputValue("");
            setIsOldAnswerCustom(false);
            setOldAnswerCustom("");

            if (currentQuestion.question_type === QuestionType.Radio) {
                const previousAnswer = Array.from(answers)[0];
                setSelectedRadioAnswer(previousAnswer || null);
                if (previousAnswer !== undefined) {
                    setHasAnswered(true);
                    if (!currentQuestion.answers.includes(previousAnswer)) {
                        setIsOldAnswerCustom(true);
                        setOldAnswerCustom(previousAnswer);
                        setCustomInputValue(previousAnswer);
                    }
                } else {
                    setHasAnswered(false);
                }
            } else if (currentQuestion.question_type === QuestionType.MultiSelect) {
                setSelectedAnswers(answers);
                const customAnswer = Array.from(answers).find(answer => !currentQuestion.answers.includes(answer));
                if (customAnswer) {
                    setIsOldAnswerCustom(true);
                    setOldAnswerCustom(customAnswer);
                    setCustomInputValue(customAnswer);
                }
            } else if (currentQuestion.question_type === QuestionType.SingleValueText || 
                      currentQuestion.question_type === QuestionType.SingleValueNumber) {
                const previousAnswer = Array.from(answers)[0] || "";
                setSingleValueAnswer(previousAnswer);
            } else if (currentQuestion.question_type === QuestionType.OpenParagraph) {
                const previousAnswer = Array.from(answers)[0] || "";
                setOpenParagraphAnswer(previousAnswer);
            }
        }
    }, [currentQuestion, surveyResponses]);

    const onSingleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setSingleValueAnswer(newValue);
        if (currentQuestion) {
            setSurveyResponses(prevResponses => 
                new Map(prevResponses).set(currentQuestion.id!, new Set([newValue]))
            );
        }
    };

    const onOpenParagraphChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setOpenParagraphAnswer(newValue);
        if (currentQuestion) {
            setSurveyResponses(prevResponses => 
                new Map(prevResponses).set(currentQuestion.id!, new Set([newValue]))
            );
        }
    };

    const onMultiSelectCardClick = (answer: string) => {
        if (ANSWERS_OPENING_INPUT.includes(answer)) {
            setChoiceBecomesInput(true);
            setCustomInputValue("");
        } else {
            setChoiceBecomesInput(false);
        }
        setSelectedAnswers(prevState => {
            const newSelectedAnswers = new Set(prevState);
            if (newSelectedAnswers.has(answer)) {
                newSelectedAnswers.delete(answer);
            } else {
                newSelectedAnswers.add(answer);
            }
            return newSelectedAnswers;
        });
    };

    const onRadioCardClick = (answer: string) => {
        if (ANSWERS_OPENING_INPUT.includes(answer)) {
            setChoiceBecomesInput(true);
            setCustomInputValue("");
            setHasAnswered(true);
        } else {
            setChoiceBecomesInput(false);
            setHasAnswered(true);
            if (currentQuestion) {
                setSurveyResponses(prevResponses => 
                    new Map(prevResponses).set(currentQuestion.id!, new Set([answer]))
                );

                // Handle gender-based question visibility
                if (currentQuestion.label === "Sesso biologico" && setQuestions) {
                    if (answer === "Uomo") {
                        // Remove the question with the label "Condizione femminile" from the questions array
                        setQuestions(prevQuestions => 
                            prevQuestions?.filter(question => question.label !== "Condizione femminile")
                        );
                    } else if (answer === "Donna") {
                        // Add the "Condizione femminile" question if it doesn't exist
                        setQuestions(prevQuestions => {
                            if (!prevQuestions) return prevQuestions;
                            const hasFemaleCondition = prevQuestions.some(q => q.label === "Condizione femminile");
                            if (!hasFemaleCondition) {
                                // Find the index of the current question
                                const currentIndex = prevQuestions.findIndex(q => q.id === currentQuestion.id);
                                if (currentIndex !== -1) {
                                    // Create a new array with the female condition question inserted after the current question
                                    const newQuestions = [...prevQuestions];
                                    newQuestions.splice(currentIndex + 1, 0, {
                                        id: "female_condition",
                                        question_text: "Hai una condizione femminile?",
                                        question_type: QuestionType.Radio,
                                        label: "Condizione femminile",
                                        updatable: true,
                                        answers: ["Sì", "No"],
                                        category: QuestionCategory.PersonalGoals,
                                        score_category: QuestionScoreCategory.ScoreCategoryOne,
                                        placeholder: null,
                                        question_subtitle: null
                                    });
                                    return newQuestions;
                                }
                            }
                            return prevQuestions;
                        });
                    }
                }

                setTimeout(() => {
                    goToNextQuestion();
                }, 100);
            }
        }

        setSelectedRadioAnswer(answer);
    };

    const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomInputValue(e.target.value);
    };

    const saveAnswer = (answers: Set<string>) => {
        if (currentQuestion) {
            setSurveyResponses(prevResponses => 
                new Map(prevResponses).set(currentQuestion.id!, answers)
            );
        }
    };

    const isConfirmButtonDisabled = () => {
        if (!currentQuestion) return true;

        switch (currentQuestion.question_type) {
            case QuestionType.SingleValueText:
            case QuestionType.SingleValueNumber:
                return singleValueAnswer === "";
            case QuestionType.Radio:
                return selectedRadioAnswer === null || 
                       (ANSWERS_OPENING_INPUT.includes(selectedRadioAnswer) && customInputValue === "");
            case QuestionType.MultiSelect:
                if (selectedAnswers.size === 0) return true;
                if (selectedAnswers.size === 1) {
                    if (ANSWERS_OPENING_INPUT.some(answer => selectedAnswers.has(answer)) && 
                        customInputValue === "") {
                        return true;
                    }
                }
                return false;
            case QuestionType.OpenParagraph:
                return false;
            default:
                return true;
        }
    };

    return {
        singleValueAnswer,
        openParagraphAnswer,
        selectedRadioAnswer,
        selectedAnswers,
        customInputValue,
        surveyResponses,
        hasAnswered,
        choiceBecomesInput,
        isOldAnswerCustom,
        oldAnswerCustom,
        onSingleValueChange,
        onOpenParagraphChange,
        onMultiSelectCardClick,
        onRadioCardClick,
        handleCustomInputChange,
        saveAnswer,
        isConfirmButtonDisabled,
        setSurveyResponses
    };
}; 